import _ from "lodash";

import { GrantType } from "prisma/cm/client";

import { getSuggestion, getNote } from "./common";
import { Proposal, ProposalValue } from "./types";

type ProposalLikeWithGrants = Pick<Proposal, "grants">;

export function getSuggestedEquity(proposal: ProposalLikeWithGrants, grantTypeId: string) {
  const proposalEquity = getProposalEquityValues(proposal, grantTypeId);
  if (!proposalEquity) {
    return null;
  }

  return getSuggestion(proposalEquity);
}

export function getProposalEquityValues<T extends ProposalLikeWithGrants>(
  proposal: T,
  grantTypeId: string,
): ProposalValue | null {
  return proposal.grants[grantTypeId] ?? null;
}

export function getProposedEquity(proposal: ProposalLikeWithGrants, grantTypeId: string) {
  const proposedEquity = getProposalEquityValues(proposal, grantTypeId);

  if (!proposedEquity) {
    return null;
  }

  return proposedEquity.actualValue;
}

export function getEquityNotes(proposal: ProposalLikeWithGrants, grantTypeId: string) {
  const proposalEquity = getProposalEquityValues(proposal, grantTypeId);
  if (!proposalEquity) {
    return null;
  }

  return getNote(proposalEquity);
}

export function getActiveGrants(proposal: ProposalLikeWithGrants, possibleGrantTypes: GrantType[]) {
  return possibleGrantTypes.filter((grantType) => {
    const hasSuggestion = Boolean(getSuggestedEquity(proposal, grantType.id));
    if (hasSuggestion) {
      return true;
    }

    const inputtedValue = getProposedEquity(proposal, grantType.id);
    if (!_.isNil(inputtedValue) && inputtedValue > 0) {
      return true;
    }

    return false;
  });
}

export function updateGrantPiece<T extends ProposalLikeWithGrants>({
  proposal,
  grantTypeId,
  newValue,
  key,
}: {
  proposal: T;
  grantTypeId: string;
  newValue: any;
  key: keyof ProposalValue;
}): T {
  const existingEquity = getProposalEquityValues(proposal, grantTypeId) ?? {
    actualValue: null,
    isManuallyEdited: false,
  };

  const newEquityValue: ProposalValue = {
    ...existingEquity,
    [key]: newValue,
  };

  proposal.grants = {
    ...proposal.grants,
    [grantTypeId]: newEquityValue,
  };

  return proposal;
}

export function getTotalProposedEquity(proposal: ProposalLikeWithGrants) {
  return _.sumBy(
    Object.entries(proposal.grants),
    ([grantTypeId]) => getProposedEquity(proposal, grantTypeId) ?? 0,
  );
}

export function getTotalSuggestedEquity(proposal: ProposalLikeWithGrants) {
  return _.sumBy(
    Object.entries(proposal.grants),
    ([grantTypeId]) => getSuggestedEquity(proposal, grantTypeId) ?? 0,
  );
}

export function updateProposedEquity<T extends ProposalLikeWithGrants>({
  proposal,
  grantTypeId,
  newValue,
  manuallyEntered = false,
}: {
  proposal: T;
  grantTypeId: string;
  newValue: number | null;
  manuallyEntered?: boolean;
}): T {
  let newProposal = updateGrantPiece({
    proposal,
    grantTypeId,
    newValue,
    key: "actualValue",
  });

  // Optionally update the manually edited flag
  if (manuallyEntered) {
    newProposal = updateGrantPiece({
      proposal,
      grantTypeId,
      newValue: manuallyEntered,
      key: "isManuallyEdited",
    });
  }

  return newProposal;
}

export function updateEquityComputedValue<T extends Proposal>({
  proposal,
  grantTypeId,
  newValue,
}: {
  proposal: T;
  grantTypeId: string;
  newValue: number | null;
}): T {
  return updateGrantPiece({
    proposal,
    grantTypeId,
    newValue,
    key: "logicComputedValue",
  });
}

export function getSumSuggestedEquity(proposals: Proposal[], grantTypes: GrantType[]): number {
  return _.sumBy(proposals, (p) => {
    return _.sumBy(grantTypes, (grantType) => {
      const suggested = getSuggestedEquity(p, grantType.id);
      if (suggested === null) {
        return 0;
      }

      return suggested;
    });
  });
}
